import { AxiosError } from "axios";
import Encoding from "encoding-japanese";
import React, { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { RouteComponentProps, withRouter } from "react-router-dom";

import DataSvc from "../../../../services/dataSvc";
import { buildCsvFilename } from "../../../../utils/csvUtils";

import "./styles.scss";

interface ICheckOwnerListProps extends RouteComponentProps<any> {
  onLoading(loadingText: string | void): void;
  onLoaded(): void;
  commonErrorHandler: (err: string) => string | undefined;
}

const CSV_COLUMNS = [
  { jp: "事業者番号", meaningKey: "csv_format_meaning_1", required: false },
  { jp: "店舗名", meaningKey: "csv_format_meaning_2", required: true },
  { jp: "都道府県コード", meaningKey: "csv_format_meaning_3", required: false },
  { jp: "アドレス", meaningKey: "csv_format_meaning_4", required: false },
  { jp: "都道府県", meaningKey: "csv_format_meaning_5", required: true },
  { jp: "住所", meaningKey: "csv_format_meaning_6", required: false },
  { jp: "自治体", meaningKey: "csv_format_meaning_7", required: true },
  { jp: "電話番号", meaningKey: "csv_format_meaning_8", required: false },
  { jp: "業種", meaningKey: "csv_format_meaning_9", required: false },
  { jp: "業種備考", meaningKey: "csv_format_meaning_10", required: false },
  { jp: "クーポン種別", meaningKey: "csv_format_meaning_11", required: false },
];

const CheckOwnerList: React.FunctionComponent<ICheckOwnerListProps> = ({
  onLoading,
  onLoaded,
  commonErrorHandler,
}) => {
  const { t: _t } = useTranslation();
  const t = useCallback(
    (key: string) => _t(`internaltoolsPage.checkOwnerList.${key}`),
    [_t]
  );
  const [selectedFile, setSelectedFile] = useState<Blob | null>(null);
  const [errorMessage, setErrorMessage] = useState<string>("");
  const [isPerformingCheck, setIsPerformingCheck] = useState<boolean>(false);
  const [resultMessage, setResultMessage] = useState("");
  const [resultContent, setResultContent] = useState<string | undefined>(
    undefined
  );

  const fetchOwnerList = async () => {
    if (!selectedFile) {
      setErrorMessage(t("please_attach_file"));
      return;
    }

    setResultMessage("");
    setIsPerformingCheck(true);
    onLoading(t("do_not_close_window"));

    const formData = new FormData();
    formData.append("file", selectedFile);

    await DataSvc.checkOwnerList(formData)
      .then((data) => {
        const { csvString } = data;

        if (!csvString) {
          setErrorMessage(t("can_not_get_scores"));
          return;
        }
        setResultContent(csvString);

        setResultMessage(t("fetch_complete"));
      })
      .catch((err) => {
        if (err instanceof AxiosError && err.response?.status === 429) {
          setErrorMessage(t("quota_exceeded"));
          return;
        }
        const filteredError = commonErrorHandler(err) ?? "";
        setErrorMessage(filteredError ?? "");
      })
      .finally(() => {
        onLoaded();
        setIsPerformingCheck(false);
      });
  };

  useEffect(() => {
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      if (isPerformingCheck) {
        event.returnValue = t("still_fetching");
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [isPerformingCheck, t]);

  const onSelectedFile = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const files: FileList | null = e.currentTarget.files;
    setSelectedFile(files !== null ? files[0] : null);
  };

  const downloadToCsv = () => {
    if (!resultContent) {
      return;
    }

    // Build standardized filename
    const filename = buildCsvFilename(
      {
        contentDescription: "check_owner_list",
      },
      _t
    );

    const blob = new Blob([resultContent], { type: "text/csv" });
    const file = window.URL.createObjectURL(blob);

    const link = document.createElement("a");
    link.setAttribute("href", file);
    link.setAttribute("download", `${filename}.csv`);

    document.body.appendChild(link);
    link.click();
  };

  const downloadToCsvWindows = () => {
    if (!resultContent) {
      return;
    }

    // Build standardized filename
    const filename = buildCsvFilename(
      {
        contentDescription: "check_owner_list",
      },
      _t
    );

    const unicodeArray = Encoding.stringToCode(resultContent);
    const sjisArray = Encoding.convert(unicodeArray, {
      to: "SJIS",
      from: "UNICODE",
      type: "array",
    });

    const blob = new Blob([new Uint8Array(sjisArray)], { type: "text/csv" });
    const file = window.URL.createObjectURL(blob);

    const link = document.createElement("a");
    link.setAttribute("href", file);
    link.setAttribute("download", `${filename}(日本語文字化け対応).csv`);

    document.body.appendChild(link);
    link.click();
  };

  return (
    <React.Fragment>
      <h1 className="main-title">{t("main_title")}</h1>
      <p className="internal-note">{t("internal_note")}</p>
      <h2 className="sub-title">{t("input")}</h2>
      <section className="card check-owner-list-input">
        <p>{t("section_1_title_1")}</p>
        <details className="csv-format-guide">
          <summary>{t("csv_format_title")}</summary>
          <div className="csv-format-guide-content">
            <p>{t("csv_format_row1")}</p>
            <p>{t("section_1_title_2")}</p>
            <div className="sample-links">
              <a
                className="btn btn-border"
                href="/samples/owner_check_sample_upload.csv"
                target="_blank"
                rel="noopener noreferrer"
                download
              >
                {t("download")}
              </a>
              <a
                className="btn btn-border"
                href="/samples/owner_check_sample_upload_windows.csv"
                target="_blank"
                rel="noopener noreferrer"
                download
              >
                {t("download_japanese_support")}
              </a>
            </div>
            <p>
              <strong>{t("section_1_content_3")}</strong>
            </p>
            <table>
              <thead>
                <tr>
                  <th>#</th>
                  <th>{t("csv_format_col_jp")}</th>
                  <th>{t("csv_format_col_meaning")}</th>
                  <th>{t("csv_format_col_required")}</th>
                </tr>
              </thead>
              <tbody>
                {CSV_COLUMNS.map(({ jp, meaningKey, required }, index) => (
                  <tr key={jp}>
                    <td>{index + 1}</td>
                    <td>{jp}</td>
                    <td>{t(meaningKey)}</td>
                    <td>
                      {t(
                        required
                          ? "csv_format_required_needed"
                          : "csv_format_required_blank"
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <p>{t("csv_format_encoding")}</p>
            <p>{t("csv_format_usage_note")}</p>
          </div>
        </details>
        <input
          onChange={onSelectedFile}
          className="input"
          type="file"
          id="file"
          name="file"
          placeholder="CSV"
          accept=".csv"
        />
        <button className="btn btn-blue" type="button" onClick={fetchOwnerList}>
          {t("execute")}
        </button>
        {errorMessage && <h3 id="error">{errorMessage.toString()}</h3>}
      </section>
      <h2 className="sub-title">{t("result_message")}</h2>
      {resultMessage && (
        <section className="card result-section">
          <p>{resultMessage}</p>
          <div className="buttons">
            <button className="btn btn-border" onClick={downloadToCsv}>
              <i className="icons icon-saveload icon-saveload-blue"></i>
              Download
            </button>
            <button className="btn btn-border" onClick={downloadToCsvWindows}>
              <i className="icons icon-saveload icon-saveload-blue"></i>
              Download (Japanese character support)
            </button>
          </div>
        </section>
      )}
    </React.Fragment>
  );
};

export default withRouter(CheckOwnerList);
